import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { IconDefinition } from '@fortawesome/free-solid-svg-icons';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-input',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, FormsModule, FontAwesomeModule],
  templateUrl: './input.component.html',
  styleUrl: './input.component.scss',
})
export class InputComponent {
  faEye = faEye;
  faEyeSlash = faEyeSlash;
  showPassword = false;

  get isPasswordToggleable() {
    return this.type === 'password';
  }

  get isCardNumber() {
    return this.inputId === 'cardNumber' || this.type === 'cardNumber';
  }

  togglePassword() {
    this.showPassword = !this.showPassword;
  }

  @Input() inputId = '';
  @Input() control = new FormControl();
  @Input() label = '';
  @Input() type = 'text';
  @Input() autocomplete = '';
  @Input() placeholder = '';
  @Input() icon?: IconDefinition;
  @Input() showErrors = true;
  @Input() maxlength?: number;
  @Input() filterType?: 'numbers' | 'letters' | 'name';

  errorMessages: Record<string, string> = {};

  constructor(private translate: TranslateService) {
    this.loadMessages();
    this.translate.onLangChange.subscribe(() => this.loadMessages());
  }

  private loadMessages(): void {
    this.errorMessages = {
      required: this.translate.instant('validation.required'),
      email: this.translate.instant('validation.email'),
      minlength: this.translate.instant('validation.minlength'),
      maxlength: this.translate.instant('validation.maxlength'),
      notSame: this.translate.instant('validation.notSame'),
      pattern: this.translate.instant('validation.pattern'),
      invalidCardType: this.translate.instant('validation.invalidCardNumber'),
      invalidCardNumber: this.translate.instant('validation.invalidCardNumber'),
      acceptTerms: this.translate.instant('validation.acceptTerms'),
    };
  }

  // Blur: mark touched+dirty so errors become visible
  onBlur() {
    this.control.markAsTouched();
    this.control.markAsDirty();
  }

  // Keypress filter
  onKeypress(event: KeyboardEvent): boolean {
    if (this.filterType === 'numbers') return /[0-9]/.test(event.key);
    if (this.filterType === 'letters') return /^[a-zA-ZáéíóúÁÉÍÓÚñÑ]$/.test(event.key);
    if (this.filterType === 'name') {
      const input = event.target as HTMLInputElement;
      if (event.key === ' ') {
        return input.value.length > 0 && !input.value.includes(' ');
      }
      return /^[a-zA-ZáéíóúÁÉÍÓÚñÑ]$/.test(event.key);
    }
    return true;
  }

  // Sanitize name input (paste / localStorage bypass protection)
  onNameInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const stripped = input.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑ ]/g, '');
    const firstSpace = stripped.indexOf(' ');
    const sanitized = firstSpace === -1
      ? stripped
      : stripped.slice(0, firstSpace + 1) + stripped.slice(firstSpace + 1).replace(/ /g, '');
    if (input.value !== sanitized) {
      input.value = sanitized;
      this.control.setValue(sanitized);
    }
  }

  // Card number: strip non-digits, format with spaces, limit to 19 digits
  onCardNumberInput(event: Event) {
    const input = event.target as HTMLInputElement;
    const numericValue = input.value.replace(/\D/g, '');
    this.control.setValue(numericValue);
    input.value = numericValue.replace(/(.{4})/g, '$1 ').trim();
  }

  onCardNumberKeydown(event: KeyboardEvent) {
    const input = event.target as HTMLInputElement;
    const key = event.key;
    if (['Backspace', 'Delete', 'Tab', 'Escape', 'Enter',
         'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(key)) {
      return;
    }
    if (!/^\d$/.test(key)) {
      event.preventDefault();
      return;
    }
    if (input.value.replace(/\s/g, '').length >= 19) {
      event.preventDefault();
    }
  }

  getDisplayValue(): string {
    const value = this.control.value || '';
    if (!this.isCardNumber) return value;
    const numericValue = value.toString().replace(/\D/g, '');
    return numericValue.replace(/(.{4})/g, '$1 ').trim();
  }
}
